import { getRequestKey } from '~/utils/products/requestKey'
import type { Product } from '~/types/models/product'
import getXOCategoryType from '~/utils/categories/getXOCategoryType'
import type { CategoryFacet, Sort } from '~/contracts/SearchRepositoryContract'
import type { CategoryType, FilterGroup } from '~/types/models/filter.model'
import { convertQueryToFilterGroups } from '~/lib/filters'

interface ProductsResponse {
  id: string
  products: Product[]
  pages: number
  totalProducts?: number
  redirects?: string[]
  facets?: FilterGroup[]
}

interface ProductsComposable {
  data: ComputedRef<{
    products: Product[] | undefined
    pages: number | undefined
    total: number | undefined
  }>
  pending: Ref<boolean>
  error: Ref<Error | null>
}

interface ProductsComposableOptions {
  page: Ref<number>
  pages: Ref<number>
  categoryTag?: Ref<string> | ComputedRef<string | undefined>
  widgetId?: ComputedRef<string | undefined>
  searchTerm?: Ref<string>
  sort?: ComputedRef<Sort | undefined>
}

export async function useProducts(options: ProductsComposableOptions): Promise<ProductsComposable> {
  const filterStore = useFilterStore()
  const productStore = useProductStore()
  const searchStore = useSearchStore()

  const { page } = options
  const filterQueries = computed(() => convertQueryToFilterGroups(useRoute().query))
  const appliedFilters = computed(() => filterQueries.value.filter(f => f.type === 'filter'))
  const appliedFacets = computed(() => filterQueries.value.filter(f => f.type === 'facet'))
  const searchTerm = options.searchTerm || computed(() => undefined)
  const categoryTag = options.categoryTag || computed(() => undefined)
  const sort = options.sort || computed(() => undefined)
  const categoryFacet = computed<CategoryFacet>(() => {
    if (categoryTag.value) {
      const type = getXOCategoryType(categoryTag.value)
      const category = type === 'category' ? categoryTag.value.replace('category-', '') : categoryTag.value

      return {
        type,
        category,
      }
    }

    return {
      type: null,
      category: null,
    }
  })

  filterStore.applyFilters(appliedFilters.value)

  onBeforeUnmount(() => {
    filterStore.reset()
  })

  const productsRequestKey = computed(() => {
    return getRequestKey({
      tag: 'products',
      searchTerm: searchTerm.value,
      filterQueries: filterQueries.value,
      page: page.value,
      sort: sort.value,
      categoryTag: categoryTag.value,
    })
  })

  useSetMaxPriceRange(categoryFacet, searchTerm)

  const isSearchRequest = computed(() => {
    return searchTerm.value !== undefined || (sort.value !== undefined && sort.value.length > 0)
  })

  // fetch product data from xo
  const {
    data: productData,
    pending,
    error,
  } = await useAsyncData(
    productsRequestKey.value,
    async () => {
      if (page.value < 1)
        return Promise.resolve(undefined)

      if (isSearchRequest.value) {
        const searchResp = await searchStore.search({
          searchTerm: searchTerm.value,
          page: page.value,
          sort: sort.value,
          filters: filterQueries.value,
          categoryType: categoryFacet.value.type,
          categoryId: categoryFacet.value.category,
        })

        return {
          id: searchResp.id || '',
          products: searchResp.products || [],
          pages: searchResp.pages,
          totalProducts: searchResp.totalProducts,
          redirects: searchResp.redirects,
          facets: searchResp.facets,
        } satisfies ProductsResponse
      }

      if (!categoryTag.value)
        return Promise.resolve(undefined)

      const hasFilterQuery = filterQueries.value.length > 0

      const facetsRequest = hasFilterQuery
        ? searchStore.fetchFacets({
          categoryId: categoryFacet.value.category,
          categoryType: categoryFacet.value.type,
          page: page.value,
          facetsOnly: true,
          filters: filterQueries.value,
        })
        : Promise.resolve(undefined)

      const [facetResp, productResp] = await Promise.all([
        facetsRequest,
        productStore.fetchProductsForCategory({
          category: categoryTag.value,
          page: page.value,
          widgetId: options.widgetId?.value,
          filters: appliedFilters.value,
          facets: appliedFacets.value,
        }),
      ])

      if (productResp && page.value > productResp.pages && productResp.pages > 0)
        throw new Error(`Page ${page.value} not found for category ${categoryTag.value}`)

      return {
        id: productResp.id || '',
        products: productResp.products || [],
        pages: productResp.pages,
        totalProducts: productResp.total,
        redirects: undefined,
        facets: facetResp?.facets,
      } as ProductsResponse
    },
    {
      lazy: true,
      watch: [page, categoryTag, searchTerm, filterQueries, sort],
      deep: false,
      /**
       * Let's track in the following issues:
       * refresh + getCachedData - Re-use getCachedData when refreshing asyncData #24332 (comment) - https://github.com/nuxt/nuxt/issues/24332#issuecomment-1827581590
       * Reactive key - useFetch & useAsyncData reactive key #21532 - https://github.com/nuxt/nuxt/issues/21532
       */
      // getCachedData: (key) => {
      //   return nuxtApp.payload.data?.[key] as ProductsResponse | undefined
      // },
    },
  )

  watch(
    error,
    (error) => {
      if (error) {
        throw createError({
          statusCode: 404,
          message: error.message,
        })
      }
    },
    {
      immediate: true,
    },
  )

  watch(
    productData,
    (data) => {
      if (!data)
        return
      useRootStore().addEarlyBirdRecord(data.id)
      options.pages.value = data.pages
      const redirect = data.redirects?.[0]

      useProductStore().currentWidgetId = options.widgetId?.value

      if (redirect)
        useSearchRedirect(redirect)
      if (data.facets)
        filterStore.setSubsetFacets(data.facets)
      else
        filterStore.setSubsetFacets()
    },
    { immediate: true },
  )

  return {
    data: computed(() => ({
      products: productData.value?.products,
      pages: productData.value?.pages,
      total: productData.value?.totalProducts,
    })),
    pending,
    error,
  }
}
